import { useState } from "react";
import { CalendarRange, Eye, Pencil, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const fmt = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

function windowLabel(item) {
  const a = fmt(item.starts_on);
  const b = fmt(item.ends_on);
  if (a && b) return `${a} to ${b}`;
  if (a) return `From ${a}`;
  if (b) return `Until ${b}`;
  return "No dates, shows whenever switched on";
}

function StatusPill({ item }) {
  if (item.live) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200" data-testid={`announcement-status-${item.id}`} data-status="live">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live for clients
      </span>
    );
  }
  if (item.enabled && !item.in_window) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200" data-testid={`announcement-status-${item.id}`} data-status="scheduled">
        On, outside its dates
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-0.5 text-[11px] font-medium text-stone-600" data-testid={`announcement-status-${item.id}`} data-status="off">
      Off
    </span>
  );
}

export function AnnouncementRow({ item, onToggle, onEdit, onPreview, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <div className="px-5 py-4 flex items-start gap-4" data-testid={`announcement-row-${item.id}`}>
      <div className="pt-1">
        <Switch
          checked={!!item.enabled}
          onCheckedChange={onToggle}
          aria-label={item.enabled ? "Switch off" : "Switch on"}
          className="data-[state=checked]:bg-[#1B4F8A]"
          data-testid={`announcement-toggle-${item.id}`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold text-slate-900 truncate" data-testid={`announcement-row-title-${item.id}`}>{item.title}</h3>
          <StatusPill item={item} />
        </div>
        <p className="mt-1 text-sm text-stone-600 line-clamp-2 whitespace-pre-line">{item.body}</p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono text-stone-400">
          <CalendarRange className="w-3 h-3" /> {windowLabel(item)}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={onPreview} title="Preview as a client" className="rounded-md p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-slate-900" data-testid={`announcement-preview-${item.id}`}>
          <Eye className="w-4 h-4" />
        </button>
        <button type="button" onClick={onEdit} title="Edit" className="rounded-md p-2 text-stone-500 transition-colors hover:bg-stone-100 hover:text-slate-900" data-testid={`announcement-edit-${item.id}`}>
          <Pencil className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => setConfirmDelete(true)} title="Delete" className="rounded-md p-2 text-stone-500 transition-colors hover:bg-rose-50 hover:text-rose-700" data-testid={`announcement-delete-${item.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="announcement-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              "{item.title}" is removed for good. Clients currently on the loading screen are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="announcement-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="announcement-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
